import React from 'react';
import styled from '@emotion/styled';
import type { TFunction } from 'i18next';

import { tokens, Link } from '@hotjar/hotsauce';
import { capitalize } from '@hotjar/insights-common/lib/string';

import type { BehaviorName, EventMarker } from '../../types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const shouldNeverBeReached = (never: never) => {
  return 'Unknown event';
};

const PREFIX = 'common:player.timeline';

const getRelevantSelector = (selector: string): string => {
  const splitSelector = selector.split('>').reverse();
  const relevantIndex = splitSelector.findIndex((s) => s.includes('#') || s.includes('.'));

  if (relevantIndex === -1 || relevantIndex + 1 === splitSelector.length) {
    return selector.slice(2).replaceAll('>', ' > ');
  }

  return splitSelector
    .slice(0, relevantIndex + 1)
    .reverse()
    .join(' > ');
};

export function getEventDescription(
  eventMarker: EventMarker,
  isDesktopRecording: boolean,
  t: TFunction,
): string {
  let description = '';
  switch (eventMarker.type) {
    case 'key_press':
      description = t(`${PREFIX}.entered-text`);
      break;
    case 'input_choice_change':
    case 'select_change':
      description = t(`${PREFIX}.selected-something`);
      break;
    case 'mouse_click':
      if (!eventMarker.text && !eventMarker.selector) {
        return t(`${PREFIX}.${isDesktopRecording ? 'mouse-click' : 'tap'}`);
      }

      return t(`${PREFIX}.${isDesktopRecording ? 'mouse-click' : 'tap'}-enriched`, {
        name: eventMarker.text ?? getRelevantSelector(eventMarker.selector as string),
      });
    case 'scroll':
      description = t(`${PREFIX}.page-scroll`);
      break;
    case 'page':
      description = t(`${PREFIX}.visited-new-page`);
      break;
    case 'viewport_resize':
      description = t(`${PREFIX}.resized-window`);
      break;
    case 'tag_recording':
      description = t(`${PREFIX}.event`, { name: eventMarker.name });
      break;
    case 'autotag_recording': {
      const behaviorName = eventMarker.subtype ?? (eventMarker.name as BehaviorName);
      if (!behaviorName) {
        break;
      }
      const AUTOTAG_PREFIX = `${PREFIX}.behavior-display-info`;
      switch (behaviorName) {
        case 'madmouse':
          description = t(`${AUTOTAG_PREFIX}.madmouse.name`);
          break;
        case 'rageclick':
          description = isDesktopRecording
            ? t(`${AUTOTAG_PREFIX}.rageclick.name`)
            : t(`${AUTOTAG_PREFIX}.rageclick.mobile-name`);
          break;
        case 'uturn':
          description = t(`${AUTOTAG_PREFIX}.uturn.name`);
          break;
        case 'wildscroll':
          description = t(`${AUTOTAG_PREFIX}.wildscroll.name`);
          break;
        case 'poll':
          if (eventMarker.autotag_properties?.e == null) {
            description = t(`${AUTOTAG_PREFIX}.poll.name`);
          } else {
            description = t(`${AUTOTAG_PREFIX}.poll.new-timeline-name`);
            description += ` = ${eventMarker.autotag_properties?.e}`;
          }
          break;
        case 'feedback':
          if (eventMarker.autotag_properties?.e == null) {
            description = t(`${AUTOTAG_PREFIX}.feedback.name`);
          } else {
            description = t(`${AUTOTAG_PREFIX}.feedback.new-timeline-name`);
            description += ' = ';
            description += t(
              `${AUTOTAG_PREFIX}.feedback.score.${eventMarker.autotag_properties?.e}`,
            );
          }
          break;
        default:
          description = shouldNeverBeReached(behaviorName);
      }
      break;
    }
    case 'heatmap_screenshot':
      description = t(`${PREFIX}.heatmap-current-screenshot`);
      break;
    case 'error':
      description = t(`${PREFIX}.error`) + eventMarker.text;
      break;
    default:
      description = shouldNeverBeReached(eventMarker.type);
      break;
  }
  return capitalize(description, true);
}

const StyledLink = styled(Link)`
  color: ${tokens.color.white};
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${tokens.color.white};
  }
`;

interface Props {
  targetUrl: string;
  eventDescription: string;
  setIsHoveringMarker: (state: boolean) => void;
}

export const EventMarkerTooltipContent = ({
  targetUrl,
  eventDescription,
  setIsHoveringMarker,
}: Props): JSX.Element => {
  return (
    <div
      onMouseEnter={() => setIsHoveringMarker(true)}
      onMouseLeave={() => setIsHoveringMarker(false)}
    >
      <StyledLink href={targetUrl} rel="noopener noreferrer" kind="inline" isExternal>
        {eventDescription}
      </StyledLink>
    </div>
  );
};
